import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ServerTile } from "@entities/server";
import type { ServerSticker, Uuid } from "@shared/api";
import { FileImage } from "@shared/ui/FileImage";
import { IconChevronDown, IconRecent, IconSearch, IconSticker } from "@shared/ui/icons";

/*
 * Часто используемые стикеры — на устройстве, как недавние знаки. Хранятся
 * номерами: имя стикера могут поменять, а номер останется.
 */
const RECENT_KEY = "airel.recent-stickers";
const RECENT_LIMIT = 8;

function recentStickers(): Uuid[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as unknown;
    return Array.isArray(raw) ? raw.filter((item): item is Uuid => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function rememberSticker(id: Uuid) {
  try {
    const next = [id, ...recentStickers().filter((item) => item !== id)].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Без хранилища «часто используемые» просто не запомнятся.
  }
}

interface Pack {
  key: string;
  title: string;
  mark: ReactNode;
  items: ServerSticker[];
}

/**
 * Выбор стикера — как в Discord: поиск сверху, колонка разделов слева
 * («Часто используемые» и сервер), разделы сворачиваются по стрелке, в
 * подвале — наведённый стикер и чей он.
 *
 * Стикеры — только этого сервера: чужие сервер не примет (как в Discord без
 * подписки), поэтому их здесь и не показываем. В личке стикеров нет вовсе.
 */
export function StickerPicker({
  stickers,
  server,
  onPick,
}: {
  stickers: ServerSticker[];
  /** Сервер канала; `null` — личная переписка. */
  server: { id: Uuid; name: string; icon_id: Uuid | null } | null;
  onPick: (id: Uuid) => void;
}) {
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState<ServerSticker | null>(null);
  const [folded, setFolded] = useState<Set<string>>(() => new Set());
  const [active, setActive] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const heads = useRef(new Map<string, HTMLElement>());

  // Как у знаков: набирают чаще, чем листают.
  useEffect(() => {
    box.current?.querySelector("input")?.focus();
  }, []);

  // Снимаются при открытии: выбор не переставляет сетку под курсором.
  const recent = useMemo(() => {
    const byId = new Map(stickers.map((sticker) => [sticker.id, sticker]));
    return recentStickers().flatMap((id) => byId.get(id) ?? []);
  }, [stickers]);

  const searching = query.trim().length > 0;
  const found = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    return needle ? stickers.filter((sticker) => sticker.name.toLocaleLowerCase("ru").includes(needle)) : [];
  }, [stickers, query]);

  const serverMark = server && <ServerTile id={server.id} name={server.name} iconId={server.icon_id} size={16} />;
  const packs: Pack[] = searching
    ? [{ key: "found", title: `Найдено по «${query.trim()}»`, mark: <IconSearch size={14} />, items: found }]
    : [
        ...(recent.length > 0
          ? [{ key: "recent", title: "Часто используемые", mark: <IconRecent size={15} />, items: recent }]
          : []),
        ...(server && stickers.length > 0 ? [{ key: "server", title: server.name, mark: serverMark, items: stickers }] : []),
      ];

  const shown = hovered ?? packs.find((pack) => pack.items.length > 0)?.items[0] ?? null;
  // Колонка — всегда полный набор разделов, и при поиске тоже: щелчок по ней поиск сбрасывает.
  const railPacks = [
    ...(recent.length > 0 ? [{ key: "recent", title: "Часто используемые" }] : []),
    ...(server && stickers.length > 0 ? [{ key: "server", title: server.name }] : []),
  ];

  function pick(sticker: ServerSticker) {
    rememberSticker(sticker.id);
    onPick(sticker.id);
  }

  function jump(key: string) {
    setQuery("");
    setFolded((value) => {
      if (!value.has(key)) return value;
      const next = new Set(value);
      next.delete(key);
      return next;
    });
    setActive(key);
    // Поиск сброшен — разделы появятся на следующей отрисовке.
    requestAnimationFrame(() => heads.current.get(key)?.scrollIntoView({ block: "start" }));
  }

  function fold(key: string) {
    setFolded((value) => {
      const next = new Set(value);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Подсветка в колонке — у раздела, чей заголовок последним ушёл под верх ленты.
  function track() {
    const list = scroller.current;
    if (!list) return;
    let current: string | null = null;
    for (const pack of packs) {
      const head = heads.current.get(pack.key);
      if (head && head.offsetTop - list.offsetTop <= list.scrollTop + 4) current = pack.key;
    }
    setActive(current ?? packs[0]?.key ?? null);
  }

  return (
    <div className="sticker-picker" ref={box}>
      <div className="emoji-search">
        <span className="emoji-search-glass" aria-hidden>
          <IconSearch size={13} />
        </span>
        <input
          type="text"
          value={query}
          placeholder="Найдите идеальный стикер"
          aria-label="Поиск стикера по названию"
          disabled={stickers.length === 0}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="sticker-picker-body">
        {railPacks.length > 0 && (
          <nav className="pick-rail" aria-label="Разделы стикеров">
            {railPacks.map((pack) => (
              <button
                key={pack.key}
                type="button"
                className={`pick-rail-item ${!searching && (active ?? packs[0]?.key) === pack.key ? "on" : ""}`}
                title={pack.title}
                onClick={() => jump(pack.key)}
              >
                {pack.key === "server" && server ? (
                  <ServerTile id={server.id} name={server.name} iconId={server.icon_id} size={32} />
                ) : (
                  <IconRecent size={18} />
                )}
              </button>
            ))}
          </nav>
        )}

        <div className="sticker-scroll" ref={scroller} onScroll={track}>
          {packs.map((pack) => (
            <section key={pack.key} className="sticker-pack">
              <button
                type="button"
                className="pick-head"
                ref={(node) => {
                  if (node) heads.current.set(pack.key, node);
                  else heads.current.delete(pack.key);
                }}
                aria-expanded={!folded.has(pack.key)}
                onClick={() => fold(pack.key)}
              >
                <span className="pick-mark">{pack.mark}</span>
                <span className="ellipsis">{pack.title}</span>
                <IconChevronDown size={14} className="pick-chevron" />
              </button>
              {!folded.has(pack.key) && (
                <div className="sticker-pack-grid">
                  {pack.items.map((sticker) => (
                    <button
                      key={sticker.id}
                      type="button"
                      className="sticker-pick-cell"
                      title={sticker.name}
                      onClick={() => pick(sticker)}
                      onMouseEnter={() => setHovered(sticker)}
                      onFocus={() => setHovered(sticker)}
                    >
                      <FileImage id={sticker.file_id} alt={sticker.name} className="sticker-pick-image" width={88} height={88} />
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))}

          {searching && found.length === 0 && <div className="emoji-nothing">Ничего не нашлось</div>}

          {!searching && packs.length === 0 && (
            <div className="sticker-empty">
              <IconSticker size={28} />
              <span>
                {server
                  ? "На этом сервере пока нет стикеров. Их добавляют в настройках сервера, раздел «Стикеры»."
                  : "Стикеры шлют на серверах: у каждого сервера они свои."}
              </span>
            </div>
          )}
        </div>
      </div>

      {shown && (
        <div className="sticker-foot">
          <FileImage id={shown.file_id} alt="" className="sticker-foot-image" width={28} height={28} />
          <span className="sticker-foot-text">
            <span className="sticker-foot-name ellipsis">{shown.name}</span>
            {server && <span className="sticker-foot-from ellipsis">из {server.name}</span>}
          </span>
        </div>
      )}
    </div>
  );
}
